import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  X, Users, Cube, ShoppingCart, AddressBook, ChartLine, Clock,
  UserSwitch, PauseCircle, PlayCircle, Warning, CheckCircle, Buildings,
  Sparkle,
} from '@phosphor-icons/react';
import useAuthStore from '../../store/authStore';
import { ProductAccessPanel } from '../../components/ProductAccessPanel';
import { DiscountLimitsPanel } from '../../components/DiscountLimitsPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';

import { confirm } from '../../components/ConfirmDialog';
import { notify } from '../../lib/notify';
import { InlineSkeleton } from '../../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Right-hand slide-out drawer for one tenant. Loaded lazily on row click —
 * we don't preload details for every tenant in the table because most rows
 * are never opened. All mutating buttons here (Suspend, Change plan,
 * Impersonate) hit the platform-level admin API and update the parent
 * `onTenantChange(updated_tenant)` callback so the tenants-table stays
 * in sync without a full refetch.
 */
export default function TenantDetailDrawer({ subdomain, onClose, onTenantChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [plans, setPlans] = useState([]);
  const { startImpersonation } = useAuthStore();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [details, plansResp] = await Promise.all([
        axios.get(`${API}/admin/tenants/${subdomain}/details`),
        axios.get(`${API}/admin/plans`),
      ]);
      setData(details.data);
      setPlans((plansResp.data.plans || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subdomain]);

  // ESC to close (matches the rest of the app's dialog UX)
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/tenants/${subdomain}`, updates);
      onTenantChange?.(r.data);
      // Re-fetch the full details response so `billing.mrr_estimate`,
      // `billing.plan_name` etc. reflect the just-changed plan. The PATCH
      // response only carries the tenant row itself, not the joined pricing.
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const doSuspend = async () => {
    await patch({ status: 'suspended' });
    setConfirmSuspend(false);
  };
  const doReactivate = () => patch({ status: 'active' });
  const changePlan = (planId) => patch({ plan: planId });

  const doImpersonate = async () => {
    if (!await confirm({ message: 
      `Impersonate ${data.president?.name || 'the President'} of ${data.tenant.company_name}?\n\n` +
      `You will be logged in as them with FULL access. Every write is audit-logged with your admin email.\n\n` +
      `A big red banner will remind you the whole time. Click 'Exit impersonation' to return to admin.`
     })) return;
    setBusy(true);
    const r = await startImpersonation(subdomain);
    setBusy(false);
    if (r.success) {
      navigate(`/t/${r.subdomain}/dashboard`);
    } else {
      notify(r.error || 'Impersonation failed');
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="tenant-detail-drawer">
      {/* Scrim */}
      <div className="flex-1 bg-black/40" onClick={onClose} data-testid="tenant-drawer-scrim" />

      {/* Panel */}
      <aside
        className="w-full max-w-[500px] h-full bg-white shadow-2xl border-l border-[#D5E8E4] flex flex-col animate-[slideIn_.25s_ease-out]"
        style={{ animationName: 'slideIn' }}
      >
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div className="p-5 border-b border-[#D5E8E4] flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#E2F3F0] grid place-items-center text-[#00a69c] shrink-0">
            <Buildings size={22} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Tenant</div>
            <div className="text-xl font-bold text-[#052A23] truncate" style={{ fontFamily: 'Outfit' }} data-testid="drawer-company-name">
              {loading ? '…' : data?.tenant?.company_name}
            </div>
            <div className="text-xs text-[#6D8E87] mt-0.5">
              <code className="text-[#00a69c]">/t/{subdomain}</code>
              {data?.tenant?.status && (
                <StatusBadge status={data.tenant.status} />
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[#F4F9F7] text-[#6D8E87]"
            data-testid="drawer-close-btn"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <InlineSkeleton />
        ) : error ? (
          <div className="p-6"><div className="medical-card p-4 border-l-4 border-red-500 text-sm text-red-700">{error}</div></div>
        ) : (
          <>
            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6" data-testid="drawer-body">
              {/* Stats grid */}
              <div>
                <SectionHead>Usage</SectionHead>
                <div className="grid grid-cols-2 gap-2.5 mt-3">
                  <StatCell icon={Users}       label="Active users"   value={data.stats.active_users}       sub={`${data.stats.pending_users} pending`} testid="stat-active-users" />
                  <StatCell icon={Cube}        label="Products"       value={data.stats.product_count}      testid="stat-products" />
                  <StatCell icon={ShoppingCart} label="Leads"          value={data.stats.lead_count}         testid="stat-leads" />
                  <StatCell icon={AddressBook} label="Customers"      value={data.stats.customer_count}     testid="stat-customers" />
                  <StatCell icon={ChartLine}   label="Quotations"     value={data.stats.quotation_count}    testid="stat-quotations" />
                  <StatCell icon={Clock}       label="Last activity"
                    value={data.stats.last_activity ? relative(data.stats.last_activity) : '—'}
                    sub={data.stats.last_activity?.slice(0, 10)}
                    testid="stat-last-activity" />
                </div>
              </div>

              {/* Billing */}
              <div>
                <SectionHead>Billing</SectionHead>
                <div className="mt-3 medical-card p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6D8E87]">Current plan</span>
                    <select
                      value={data.billing.plan_id || ''}
                      onChange={(e) => changePlan(e.target.value)}
                      disabled={busy}
                      className="text-sm font-semibold text-[#052A23] px-2 py-1 border border-[#D5E8E4] rounded-md bg-white cursor-pointer"
                      data-testid="drawer-plan-select"
                    >
                      {!plans.find((p) => p.id === data.billing.plan_id) && (
                        <option value={data.billing.plan_id || ''}>{data.billing.plan_id || 'unknown'}</option>
                      )}
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} — ${p.monthly_price}/user</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6D8E87]">Monthly revenue (est.)</span>
                    <span className="text-lg font-black text-[#00a69c]" style={{ fontFamily: 'Outfit' }} data-testid="drawer-mrr">
                      ${data.billing.mrr_estimate.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6D8E87]">Annualised</span>
                    <span className="text-sm text-[#3B5A54]" data-testid="drawer-arr">
                      ${data.billing.arr_estimate.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#6D8E87] leading-tight border-t border-[#D5E8E4] pt-2">
                    Estimate = plan price × active users. Doesn&apos;t account for annual discounts or negotiated custom deals.
                  </div>
                </div>
              </div>

              {/* President identity */}
              {data.president && (
                <div>
                  <SectionHead>President account</SectionHead>
                  <div className="mt-3 medical-card p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E2F3F0] grid place-items-center text-[#00a69c] font-bold text-sm">
                        {data.president.name?.charAt(0) || 'P'}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#052A23] truncate">{data.president.name}</div>
                        <div className="text-xs text-[#6D8E87] truncate">{data.president.email}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div>
                <SectionHead>Metadata</SectionHead>
                <div className="mt-3 medical-card p-4 text-xs text-[#3B5A54] space-y-1.5">
                  <MetaRow label="Tenant ID" value={<code className="text-[10px]">{data.tenant.id}</code>} />
                  <MetaRow label="Created" value={data.tenant.created_at?.slice(0, 10)} />
                  <MetaRow label="Created by" value={data.tenant.created_by || 'system'} />
                  <MetaRow label="Updated" value={data.tenant.updated_at?.slice(0, 10) || '—'} />
                </div>
              </div>
            </div>

            {/* Actions footer */}
            <div className="p-4 border-t border-[#D5E8E4] bg-[#F4F9F7] space-y-2">
              {/* Vooxy Copilot toggle (super-admin override) */}
              <CopilotTenantToggle
                tenantId={data.tenant?.id}
                initialEnabled={data.tenant?.copilot_enabled !== false}
              />
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full px-4 py-2 text-sm font-semibold rounded-lg border border-[#D5E8E4] bg-white text-[#052A23] hover:bg-[#E8F5F3] transition-colors" data-testid="drawer-product-access-btn">
                    Product upload access…
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle>Product Upload Access · {data.tenant?.company_name}</DialogTitle></DialogHeader>
                  <ProductAccessPanel basePath={`/admin/tenants/${data.tenant?.id}/product-upload-access`} />
                </DialogContent>
              </Dialog>
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full px-4 py-2 text-sm font-semibold rounded-lg border border-[#D5E8E4] bg-white text-[#052A23] hover:bg-[#E8F5F3] transition-colors" data-testid="drawer-discount-limits-btn">
                    Discount limits by role…
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl">
                  <DialogHeader><DialogTitle>Discount limits · {data.tenant?.company_name}</DialogTitle></DialogHeader>
                  <DiscountLimitsPanel basePath={`/admin/tenants/${data.tenant?.id}/discount-limits`} />
                </DialogContent>
              </Dialog>
              <button
                onClick={doImpersonate}
                disabled={busy || !data.president || data.tenant.status !== 'active'}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00a69c] hover:bg-[#008b83] text-white text-sm font-bold rounded-lg shadow disabled:bg-[#00a69c]/40 disabled:cursor-not-allowed transition"
                data-testid="drawer-impersonate-btn"
              >
                <UserSwitch size={16} weight="bold" />
                Impersonate {data.president?.name?.split(' ')[0] || 'President'}
              </button>
              {data.tenant.status === 'suspended' && (
                <div className="text-[11px] text-amber-600 text-center">
                  Reactivate the tenant to impersonate.
                </div>
              )}

              {data.tenant.status === 'active' ? (
                confirmSuspend ? (
                  <div className="flex gap-2">
                    <button
                      onClick={doSuspend}
                      disabled={busy}
                      className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                      data-testid="drawer-suspend-confirm"
                    >
                      Yes — Suspend
                    </button>
                    <button
                      onClick={() => setConfirmSuspend(false)}
                      disabled={busy}
                      className="flex-1 px-3 py-2 border border-[#D5E8E4] text-[#3B5A54] text-xs font-medium rounded-lg hover:bg-white"
                      data-testid="drawer-suspend-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmSuspend(true)}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-red-200 text-red-700 hover:bg-red-50 text-sm font-medium rounded-lg"
                    data-testid="drawer-suspend-btn"
                  >
                    <PauseCircle size={16} weight="bold" />
                    Suspend tenant (blocks all logins)
                  </button>
                )
              ) : (
                <button
                  onClick={doReactivate}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium rounded-lg"
                  data-testid="drawer-reactivate-btn"
                >
                  <PlayCircle size={16} weight="bold" />
                  Reactivate tenant
                </button>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

const SectionHead = ({ children }) => (
  <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold flex items-center gap-2">
    <span className="w-8 h-[1px] bg-[#92400E]/40" />{children}
  </div>
);

const StatCell = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="medical-card p-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#6D8E87]">
      <Icon size={12} /> {label}
    </div>
    <div className="text-2xl font-black text-[#052A23] leading-none mt-1" style={{ fontFamily: 'Outfit' }}>
      {value ?? '—'}
    </div>
    {sub && <div className="text-[10px] text-[#6D8E87] mt-1">{sub}</div>}
  </div>
);

const MetaRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[#6D8E87]">{label}</span>
    <span className="text-[#052A23] truncate">{value ?? '—'}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = status === 'active'
    ? { icon: CheckCircle, bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Active' }
    : { icon: Warning, bg: 'bg-red-100', text: 'text-red-700', label: 'Suspended' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.text}`} data-testid="drawer-status-badge">
      <Icon size={10} weight="fill" /> {cfg.label}
    </span>
  );
};

function relative(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  const day = 86400000;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < day) return `${Math.floor(diff / 3600_000)}h ago`;
  return `${Math.floor(diff / day)}d ago`;
}

/**
 * CopilotTenantToggle — super-admin pill-switch for the Vooxy Copilot
 * on this tenant. Mirrors the President's toggle in Company Settings but
 * reachable from the platform admin console so you can bulk-tweak
 * tenants during the beta without impersonating each one.
 */
function CopilotTenantToggle({ tenantId, initialEnabled }) {
  const [enabled, setEnabled] = useState(!!initialEnabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Keep local state in sync when the drawer is reused for a different tenant.
  useEffect(() => { setEnabled(!!initialEnabled); }, [tenantId, initialEnabled]);

  const toggle = async () => {
    if (!tenantId) return;
    const next = !enabled;
    setSaving(true);
    setError('');
    try {
      const r = await axios.put(`${API}/admin/copilot/tenants/${tenantId}`, { enabled: next });
      setEnabled(r.data?.enabled !== false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border border-[#D5E8E4] bg-white"
      data-testid="drawer-copilot-toggle"
    >
      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0 mt-0.5">
        <Sparkle size={14} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-[#052A23]">Vooxy Copilot (AI Assistant)</div>
        <div className="text-[10px] text-[#6D8E87] mt-0.5 leading-snug">
          {enabled
            ? 'Enabled — floating chat visible to all users on this tenant.'
            : 'Disabled — widget hidden; chat endpoint returns 403.'}
        </div>
        {error && <div className="text-[10px] text-red-600 mt-1">{error}</div>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
        <input
          type="checkbox"
          checked={enabled}
          onChange={toggle}
          disabled={saving}
          className="sr-only peer"
          data-testid="drawer-copilot-toggle-input"
        />
        <div className="w-10 h-5 bg-slate-300 rounded-full peer peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-disabled:opacity-50"></div>
      </label>
    </div>
  );
}

